<template>
  <div>
    <notification-panels :showError="error" :errorMessage="error" :showSuccess="false" :fluid="true" />
    <div class="conference">
      <div v-if="!showVideoConference" class="container">
        <div v-if="loading && !error">
          <spinner line-fg-color="#148898" line-bg-color="#99bfbf" size="large" :speed="1.5" />
          <div class="loading-information">{{ $t("conference.initializing") }}..</div>
        </div>
        <div class="card-wrapper" v-if="!loading">
          <div class="card" v-if="!error">
            <div class="card-body">
              <h5 class="card-title">{{ roomName }}</h5>
              <h6 v-if="roomDate && conferenceSettings.showDateTimeInTitle" class="card-subtitle mb-2 text-muted">{{ $d(new Date(roomDate), "long") }}</h6>
              <!-- <h6 class="card-subtitle mb-2 text-muted">{{ $t("conference.roomNumber") }}: {{ roomId }}</h6> -->
              <p class="card-text">
                {{ $t("conference.tutorial") }}
              </p>
              <div class="form-group form-check">
                <input type="checkbox" class="form-check-input" id="cb-participant-only" v-model="participantOnly" />
                <label class="form-check-label" for="cb-participant-only">{{ $t("participantOnly") }}</label>
              </div>
              <button autofocus class="btn btn-primary" @click="goToConference">{{ $t("conference.enterConference") }}</button>
              <router-link class="btn btn-secondary ml-2" :to="{ name: 'conference-moderator' }">{{ $t("openCaseOverview") }}</router-link>
            </div>
          </div>
          <div class="card" v-if="error">
            <div class="card-body">
              <h5 class="card-title">{{ roomName }}</h5>
              <h6 v-if="roomDate && conferenceSettings.showDateTimeInTitle" class="card-subtitle mb-2 text-muted">{{ $d(new Date(roomDate), "long") }}</h6>
              <p class="card-text">
                {{ $t("conference.notAllowed") }}
              </p>
              <router-link tag="button" class="btn btn-primary" :to="{ name: 'conference-overview' }">{{ $t("conference.backToOverview") }}</router-link>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showVideoConference" class="container-fluid">
        <video-conference
          :showEntries="showEntries"
          :entries="entries"
          :ratioX="ratioX"
          :ratioY="ratioY"
          :cutVideoStreams="conferenceSettings.cutVideoStreams"
          :bitrate="conferenceSettings.bitrate"
          :showVideoInitially="conferenceSettings.showVideo"
          :enableAudioInitially="conferenceSettings.enableAudioInitially"
          :turnUrl="conferenceSettings.turnUrl"
          :turnUser="conferenceSettings.turnUser"
          :turnSecret="conferenceSettings.turnSecret"
          :room="roomId"
          :roomToken="roomToken"
          :roomName="roomName"
          :roomDate="roomDate"
          :showRoomDate="conferenceSettings.showDateTimeInTitle"
          :maxNumberOfVideos="conferenceSettings.maxNumberOfVideos"
          :participantOnly="participantOnly"
          @updateParticipantOnly="updateParticipantOnly"
        />
      </div>
    </div>
  </div>
</template>

<script>
import NotificationPanels from "@/components/ui/NotificationPanels";
import VideoConference from "@/components/video-conference/VideoConference";
import config from "@/config/config";
import { getAdhocRoom, addUserToAdhocRoomByToken, getRoomsAccessible, getAccessTokenForRoom } from "@/api/video-api";
import { handleAxiosError } from "@/util/error-util";

import Spinner from "vue-simple-spinner";
import { mapState } from "vuex";

export default {
  data() {
    return {
      loading: true,
      entries: [],
      error: null,
      roomId: null,
      roomName: null,
      roomDate: null,
      roomToken: null,
      showVideoConference: false,
      showEntries: true,
      rooms: null,
      participantOnly: false
    };
  },

  computed: {
    ...mapState({
      token: state => state.authentication.keycloak.token,
      conferenceSettings: state => state.settings.conferenceSettings
    }),

    demo() {
      return config.DEMO;
    },

    deactivateDocumentation() {
      return config.DEACTIVATE_DOCUMENTATION;
    },

    ratioX() {
      if (this.conferenceSettings && this.conferenceSettings.aspectRatio && typeof this.conferenceSettings.aspectRatio === "string") {
        let ratioX = this.conferenceSettings.aspectRatio.split(":")[0];
        if (ratioX && parseInt(ratioX)) {
          return parseInt(ratioX);
        }
      }
      return 16;
    },

    ratioY() {
      if (this.conferenceSettings && this.conferenceSettings.aspectRatio && typeof this.conferenceSettings.aspectRatio === "string") {
        let ratioY = this.conferenceSettings.aspectRatio.split(":")[1];
        if (ratioY && parseInt(ratioY)) {
          return parseInt(ratioY);
        }
      }
      return 10;
    }
  },

  methods: {
    handleError(error) {
      this.error = handleAxiosError(error, this);
      this.loading = false;
      window.scrollTo(0, 0);
    },

    updateParticipantOnly(participantOnly) {
      this.participantOnly = participantOnly;
    },

    async loadAdhocConference() {
      try {
        this.roomName = this.conferenceSettings.persistentRoomName;
        this.showEntries = false;
        this.roomId = (await getAdhocRoom(this.token)).data.room;
        await addUserToAdhocRoomByToken(this.token);
        this.roomToken = (await getAccessTokenForRoom(this.roomId, this.token)).data.token;
        this.loading = false;
      } catch (e) {
        this.handleError(e);
      }
    },

    async loadVideoConference() {
      try {
        this.roomId = parseInt(this.$route.params.room);
        this.rooms = (await getRoomsAccessible(this.token)).data.entity;
        const currentRoom = this.rooms.find(room => room.janusId === this.roomId);
        if (currentRoom && currentRoom.tumorConference) {
          this.roomName = currentRoom.tumorConference.description;
          this.roomDate = currentRoom.tumorConference.date;
          this.entries = currentRoom.tumorConference.entries;
        }
        this.roomToken = (await getAccessTokenForRoom(this.roomId, this.token)).data.token;
        this.loading = false;
      } catch (e) {
        this.handleError(e);
      }
    },

    goToConference() {
      this.showVideoConference = true;
    },

    async initialize() {
      if (this.$route.params.room) {
        if (this.$route.params.room === "adhoc") {
          this.loadAdhocConference();
        } else {
          await this.loadVideoConference();
        }
      } else {
        this.$router.push({ name: "conference-overview" });
      }
    }
  },

  mounted() {
    this.initialize();
  },

  async beforeRouteLeave(to, from, next) {
    if (!this.showVideoConference) {
      next();
      return;
    }

    const answer = await this.$bvModal.msgBoxConfirm(this.$t("reallyLeaveConference"), {
      title: this.$t("pleaseConfirm"),
      okTitle: this.$t("conference.leaveConference"),
      cancelTitle: this.$t("cancel")
    });
    if (!answer) {
      next(false);
    } else {
      next();
    }
  },

  components: {
    Spinner,
    NotificationPanels,
    VideoConference
  }
};
</script>

<style lang="scss" scoped>
.conference {
  padding-top: 15px;
}

.card-wrapper {
  display: flex;
  justify-content: center;

  .card {
    max-width: 32rem;

    .card-title {
      color: $vitu-green;
    }
  }
}

.loading-information {
  display: flex;
  justify-content: center;
  padding-top: 15px;
  font-size: 1.2rem;
}
</style>
